import type { IWebsiteBuilderFont, IWebsiteBuilderTheme, IWebsiteBuilderWebsiteVersionGlobalStyles } from "redux/reducers/models/websiteBuilder";

type TWebsiteBuilderRootCSSVariable = `--wb-theme-${string}` | `--wb-global-${string}`;
export type TWebsiteBuilderRootCSSVariables = Record<TWebsiteBuilderRootCSSVariable, string>;

const WB_THEME_PRIMARY: TWebsiteBuilderRootCSSVariable = "--wb-theme-primary";
const WB_THEME_SECONDARY: TWebsiteBuilderRootCSSVariable = "--wb-theme-secondary";
const WB_THEME_TERTIARY: TWebsiteBuilderRootCSSVariable = "--wb-theme-tertiary";
const WB_THEME_QUATERNARY: TWebsiteBuilderRootCSSVariable = "--wb-theme-quaternary";

const WB_GLOBAL_HEADING_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-heading-color";
const WB_GLOBAL_HEADING_FONT_FAMILY: TWebsiteBuilderRootCSSVariable = "--wb-global-heading-font-family";

const WB_GLOBAL_BODY_TEXT_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-body-text-color";
const WB_GLOBAL_BODY_TEXT_FONT_FAMILY: TWebsiteBuilderRootCSSVariable = "--wb-global-body-text-font-family";
const WB_GLOBAL_BODY_TEXT_FONT_SIZE: TWebsiteBuilderRootCSSVariable = "--wb-global-body-text-font-size";

const WB_GLOBAL_BUTTON_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-button-color";
const WB_GLOBAL_BUTTON_BACKGROUND_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-button-background-color";
const WB_GLOBAL_BUTTON_BORDER_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-button-border-color";
const WB_GLOBAL_BUTTON_HOVER_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-button-hover-color";
const WB_GLOBAL_BUTTON_HOVER_BACKGROUND_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-button-hover-background-color";
const WB_GLOBAL_BUTTON_HOVER_BORDER_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-button-hover-border-color";
const WB_GLOBAL_BUTTON_HOVER_TRANSITION_DURATION: TWebsiteBuilderRootCSSVariable = "--wb-global-button-hover-transition-duration";
const WB_GLOBAL_LINK_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-link-color";
const WB_GLOBAL_LINK_HOVER_COLOR: TWebsiteBuilderRootCSSVariable = "--wb-global-link-hover-color";

const defaultTheme: IWebsiteBuilderTheme = {
  id: 0,
  title: "Default",
  primary_colour: "#000000",
  secondary_colour: "#C8C8C8",
  tertiary_colour: "#E1E1E1",
  quaternary_colour: "#FFFFFF",
};

export function websiteBuilderGenerateRootCssVariables(
  theme: IWebsiteBuilderTheme,
  globalStyles: IWebsiteBuilderWebsiteVersionGlobalStyles,
  fonts: IWebsiteBuilderFont[],
): TWebsiteBuilderRootCSSVariables {
  const themeToApply = theme ?? defaultTheme;

  const vars: TWebsiteBuilderRootCSSVariables = {
    [WB_THEME_PRIMARY]: themeToApply.primary_colour,
    [WB_THEME_SECONDARY]: themeToApply.secondary_colour,
    [WB_THEME_TERTIARY]: themeToApply.tertiary_colour,
    [WB_THEME_QUATERNARY]: themeToApply.quaternary_colour,
  };

  if (!globalStyles) {
    return vars;
  }

  const {
    heading_color,
    heading_font_id,
    body_text_color,
    body_text_font_id,
    body_text_font_size,
    button_color,
    button_background_color,
    button_border_color,
    button_hover_color,
    button_hover_background_color,
    button_hover_border_color,
    button_hover_transition,
    link_color,
    link_hover_color,
  } = globalStyles;

  if (heading_color) {
    vars[WB_GLOBAL_HEADING_COLOR] = heading_color;
  }
  if (heading_font_id && fonts) {
    const headingFont = fonts.find(font => font.id === heading_font_id);

    if (headingFont) {
      vars[WB_GLOBAL_HEADING_FONT_FAMILY] = `"${headingFont.font_family}", ${headingFont.generic_font_family}`;
    }
  }

  if (body_text_color) {
    vars[WB_GLOBAL_BODY_TEXT_COLOR] = body_text_color;
  }
  if (body_text_font_id && fonts) {
    const bodyTextFont = fonts.find(font => font.id === body_text_font_id);

    if (bodyTextFont) {
      vars[WB_GLOBAL_BODY_TEXT_FONT_FAMILY] = `"${bodyTextFont.font_family}", ${bodyTextFont.generic_font_family}`;
    }
  }
  if (body_text_font_size) {
    vars[WB_GLOBAL_BODY_TEXT_FONT_SIZE] = `${body_text_font_size}px`;
  }

  if (button_color) {
    vars[WB_GLOBAL_BUTTON_COLOR] = button_color;
  }
  if (button_background_color) {
    vars[WB_GLOBAL_BUTTON_BACKGROUND_COLOR] = button_background_color;
  }
  if (button_border_color) {
    vars[WB_GLOBAL_BUTTON_BORDER_COLOR] = button_border_color;
  }
  if (button_hover_color) {
    vars[WB_GLOBAL_BUTTON_HOVER_COLOR] = button_hover_color;
  }
  if (button_hover_background_color) {
    vars[WB_GLOBAL_BUTTON_HOVER_BACKGROUND_COLOR] = button_hover_background_color;
  }
  if (button_hover_border_color) {
    vars[WB_GLOBAL_BUTTON_HOVER_BORDER_COLOR] = button_hover_border_color;
  }
  if (button_hover_transition === "smooth") {
    vars[WB_GLOBAL_BUTTON_HOVER_TRANSITION_DURATION] = "0.2s";
  }
  if (link_color) {
    vars[WB_GLOBAL_LINK_COLOR] = link_color;
  }
  if (link_hover_color) {
    vars[WB_GLOBAL_LINK_HOVER_COLOR] = link_hover_color;
  }

  return vars;
}